import { Injectable, ComponentRef, createComponent, ApplicationRef, Injector, EmbeddedViewRef } from '@angular/core';
import { DatlazToasterComponent, DatlazToastData } from '../components/reusable/datlaz-toaster/datlaz-toaster.component';

@Injectable({
  providedIn: 'root'
})
export class DatlazToasterService {
  private toasts: Map<string, ComponentRef<DatlazToasterComponent>> = new Map();
  private container: HTMLElement | null = null;

  constructor(
    private appRef: ApplicationRef,
    private injector: Injector
  ) {
    this.createContainer();
  }

  private createContainer() {
    this.container = document.createElement('div');
    this.container.className = 'datlaz-toaster-container';
    this.container.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      pointer-events: none;
    `;
    document.body.appendChild(this.container);
  }

  show(toastData: Omit<DatlazToastData, 'id'>): string {
    const id = this.generateId();
    const fullToastData: DatlazToastData = {
      ...toastData,
      id,
      duration: toastData.duration || 30000 // Default 30 seconds
    };

    const componentRef = createComponent(DatlazToasterComponent, {
      environmentInjector: this.appRef.injector,
      elementInjector: this.injector
    });

    componentRef.instance.toast = fullToastData;
    
    // Handle close event
    componentRef.instance.close.subscribe((toastId: string) => {
      this.removeToast(toastId);
    });

    // Handle click event
    componentRef.instance.click.subscribe((toast: DatlazToastData) => {
      // Additional click handling can be added here
    });

    // Attach to DOM
    const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0];
    this.container?.appendChild(domElem);
    
    // Add pointer events to the toast itself
    domElem.style.pointerEvents = 'auto';
    
    this.toasts.set(id, componentRef);
    this.appRef.attachView(componentRef.hostView);

    return id;
  }

  success(title: string, content: string, options?: Partial<DatlazToastData>): string {
    return this.show({
      title,
      content,
      type: 'success',
      ...options
    });
  }

  error(title: string, content: string, options?: Partial<DatlazToastData>): string {
    return this.show({
      title,
      content,
      type: 'error',
      ...options
    });
  }

  warning(title: string, content: string, options?: Partial<DatlazToastData>): string {
    return this.show({
      title,
      content,
      type: 'warning',
      ...options
    });
  }

  info(title: string, content: string, options?: Partial<DatlazToastData>): string {
    return this.show({
      title,
      content,
      type: 'info',
      ...options
    });
  }

  removeToast(id: string) {
    const componentRef = this.toasts.get(id);
    if (componentRef) {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
      this.toasts.delete(id);
    }
  }

  clear() {
    this.toasts.forEach((componentRef, id) => {
      this.removeToast(id);
    });
  }

  private generateId(): string {
    return 'toast_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
}
